import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Instagram, Quote as QuoteIcon } from "lucide-react";
import { motion } from "framer-motion";
import { QUOTES, SOCIALS } from "../data/mock";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ShareMenu } from "../components/ShareMenu";
import { EngageCard } from "../components/EngageCard";
import { useSEO } from "../hooks/useSEO";

/*
 * Quotes can be short or up to ~10 lines. The heading scales down as the
 * quote grows so long quotes stay readable and the meaning stays the star.
 */
const quoteSize = (len) => {
  if (len < 90) return "text-3xl md:text-5xl leading-[1.15]";
  if (len < 160) return "text-2xl md:text-[38px] leading-[1.22]";
  if (len < 260) return "text-xl md:text-[30px] leading-[1.3]";
  return "text-lg md:text-2xl leading-[1.4]";
};

export const QuoteDetail = () => {
  const { slug } = useParams();
  const quote = QUOTES.find((q) => q.slug === slug);

  useSEO({
    title: quote ? `"${quote.text}" Hunar Syal` : "Quote not found",
    description: quote?.meaning?.slice(0, 160),
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  if (!quote) return <Navigate to="/" replace />;
  const others = QUOTES.filter((q) => q.id !== quote.id);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div data-testid="quote-detail-page" style={{ background: "var(--brand-cream)" }}>
      <Navbar />

      <article className="pt-28 md:pt-36 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-14">
          <div className="min-w-0 max-w-2xl mx-auto lg:mx-0">
          <Link
            to="/#quotes"
            data-testid="quote-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold mb-10 hover:opacity-70 transition-opacity"
            style={{ color: "var(--brand-muted)" }}
          >
            <ArrowLeft size={15} strokeWidth={2} />
            Back to quotes
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <QuoteIcon
              size={36}
              strokeWidth={1.4}
              style={{ color: "var(--brand-accent)" }}
            />
            <blockquote
              className={`mt-5 ${quoteSize(quote.text.length)} font-bold tracking-tight`}
              style={{ color: "var(--brand-ink)" }}
              data-testid="quote-text"
            >
              &ldquo;{quote.text}&rdquo;
            </blockquote>
            <p
              className="mt-6 text-sm font-semibold"
              style={{ color: "var(--brand-accent)" }}
            >
              Hunar Syal
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-14 pt-10"
            style={{ borderTop: "1px solid var(--brand-line)" }}
          >
            <p
              className="text-[11px] font-semibold uppercase tracking-[0.3em]"
              style={{ color: "var(--brand-sage)" }}
            >
              The meaning
            </p>
            <p
              className="mt-5 text-lg md:text-xl leading-[1.7]"
              style={{ color: "var(--brand-ink-soft)" }}
              data-testid="quote-meaning"
            >
              {quote.meaning}
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-3">
              <a
                href={SOCIALS.instagramDM}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-get-in-touch"
                className="inline-flex items-center gap-2 rounded-full btn-primary font-semibold px-6 py-3"
              >
                <Instagram size={17} strokeWidth={2} />
                Get in touch
              </a>
              <ShareMenu
                title="A quote by Hunar Syal"
                text={quote.text}
                url={shareUrl}
                testIdPrefix="quote-share"
              />
            </div>
          </motion.div>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-28">
              <EngageCard />
            </div>
          </div>

          <div className="lg:hidden mt-12 max-w-2xl mx-auto">
            <EngageCard />
          </div>
        </div>
      </article>

      <section
        className="py-20 px-6 md:px-12 lg:px-20"
        style={{
          background: "var(--brand-cream-soft)",
          borderTop: "1px solid var(--brand-line)",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <h3
            className="text-3xl md:text-4xl font-bold tracking-tight mb-10"
            style={{ color: "var(--brand-ink)" }}
          >
            More quotes
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {others.map((o) => (
              <Link
                key={o.id}
                to={`/quotes/${o.slug}`}
                data-testid={`quote-related-${o.id}`}
                className="group block rounded-[16px] p-6 transition-transform duration-300 hover:-translate-y-1"
                style={{
                  background: "var(--brand-cream)",
                  border: "1px solid var(--brand-line)",
                }}
              >
                <QuoteIcon
                  size={16}
                  strokeWidth={1.6}
                  className="mb-3"
                  style={{ color: "var(--brand-accent)" }}
                />
                <p
                  className="text-base md:text-[17px] font-semibold tracking-tight leading-snug line-clamp-4"
                  style={{ color: "var(--brand-ink)" }}
                >
                  &ldquo;{o.text}&rdquo;
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuoteDetail;
